import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get, post } from '@/lib/api';

export interface AssetTemplate {
  id: string;
  name: string;
  description?: string;
  asset_ids?: string[];
  category?: string;
  category_ids?: string[];
  tags?: string;
  cover_image?: string;
  cover_media_type?: string;
  template_type?: string;
  source_template_id?: string;
  is_active?: boolean;
  is_offline?: boolean;
  is_featured?: boolean;
  is_homepage_featured?: boolean;
  is_public?: boolean;
  is_detection?: boolean;
  volcano_sync_status?: string;
  sort_order?: number;
  i18n_content?: Record<string, unknown>;
  language_regions?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface AssetCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  origin?: string;
  parent_id?: string;
  sort_order?: number;
  template_type?: string;
  user_id?: string;
  category_type?: string;
  created_at?: string;
  updated_at?: string;
  children?: AssetCategory[];
}

export interface SearchAssetsParams {
  keyword?: string;
  category?: string;
  category_ids?: string;
  template_type?: string;
  is_active?: boolean;
  is_featured?: boolean;
  is_public?: boolean;
  page?: number;
  page_size?: number;
}

export interface CreateAssetPayload {
  name: string;
  scope?: 'personal' | 'team';
  folder_id?: string;
  meta_width?: number;
  meta_height?: number;
  description?: string;
  category?: string;
  category_ids?: string[];
  tags?: string;
  cover_image?: string;
  cover_media_type?: string;
  template_type?: string;
  source_template_id?: string;
  is_active?: boolean;
  is_offline?: boolean;
  is_featured?: boolean;
  is_homepage_featured?: boolean;
  is_public?: boolean;
  sort_order?: number;
  i18n_content?: Record<string, unknown>;
  language_regions?: string[];
  is_detection?: boolean;
}

export interface UpdateAssetPayload extends Partial<Omit<CreateAssetPayload, 'is_detection'>> {
  id: string;
  approved_by?: string;
}

export interface SearchAssetCategoriesParams {
  keyword?: string;
  origin?: string;
  category_type?: string;
  template_type?: string;
  user_id?: string;
  parent_id?: string;
  page?: number;
  page_size?: number;
}

export interface AssetCategoryTreeParams {
  origin?: string;
  category_type?: string;
  template_type?: string;
  user_id?: string;
}

export interface CreateAssetCategoryPayload {
  name: string;
  description?: string;
  icon?: string;
  origin?: string;
  parent_id?: string;
  sort_order?: number;
  template_type?: string;
  user_id?: string;
  category_type?: string;
}

export interface UpdateAssetCategoryPayload extends Partial<CreateAssetCategoryPayload> {
  id: string;
}

interface AssetsState {
  items: AssetTemplate[];
  currentAsset: AssetTemplate | null;
  categories: AssetCategory[];
  categoryTree: AssetCategory[];
  currentCategory: AssetCategory | null;
  loading: boolean;
  detailLoading: boolean;
  saving: boolean;
  deletingId: string | null;
  syncingStatusId: string | null;
  actingId: string | null;
  loadingCategories: boolean;
  loadingCategoryTree: boolean;
  categoryDetailLoading: boolean;
  savingCategory: boolean;
  deletingCategoryId: string | null;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const initialState: AssetsState = {
  items: [],
  currentAsset: null,
  categories: [],
  categoryTree: [],
  currentCategory: null,
  loading: false,
  detailLoading: false,
  saving: false,
  deletingId: null,
  syncingStatusId: null,
  actingId: null,
  loadingCategories: false,
  loadingCategoryTree: false,
  categoryDetailLoading: false,
  savingCategory: false,
  deletingCategoryId: null,
  error: null,
  total: 0,
  page: 1,
  pageSize: 20,
  totalPages: 0,
};

function buildSearchParams(
  params?: { [key: string]: string | number | boolean | undefined }
) {
  const searchParams = new URLSearchParams();

  if (!params) {
    return searchParams;
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    searchParams.set(key, String(value));
  });

  return searchParams;
}

function unwrapApiData<T>(response: T | { data: T }): T {
  if (
    response &&
    typeof response === 'object' &&
    'data' in response
  ) {
    return response.data;
  }

  return response;
}

export const searchAssets = createAsyncThunk(
  'assets/searchAssets',
  async (params?: SearchAssetsParams) => {
    const query = buildSearchParams({
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 20,
      keyword: params?.keyword,
      category: params?.category,
      category_ids: params?.category_ids,
      template_type: params?.template_type,
      is_active: params?.is_active,
      is_featured: params?.is_featured,
      is_public: params?.is_public,
    });

    const response = await get<{
      list: AssetTemplate[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    } | {
      data: {
        list: AssetTemplate[];
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
      };
    }>(`/api/assets/public?${query.toString()}`);

    return unwrapApiData(response);
  }
);

export const searchPublicAssets = createAsyncThunk(
  'assets/searchPublicAssets',
  async (params?: SearchAssetsParams) => {
    const query = buildSearchParams({
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 20,
      keyword: params?.keyword,
      category: params?.category,
      category_ids: params?.category_ids,
      template_type: params?.template_type,
      is_active: params?.is_active,
      is_featured: params?.is_featured,
      is_public: params?.is_public,
    });

    const response = await get<{
      list: AssetTemplate[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    } | {
      data: {
        list: AssetTemplate[];
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
      };
    }>(`/api/assets/public?${query.toString()}`);

    return unwrapApiData(response);
  }
);

export const searchPersonalAssets = createAsyncThunk(
  'assets/searchPersonalAssets',
  async (params?: SearchAssetsParams) => {
    const query = buildSearchParams({
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 20,
      keyword: params?.keyword,
      category: params?.category,
      category_ids: params?.category_ids,
      template_type: params?.template_type,
      is_active: params?.is_active,
      is_featured: params?.is_featured,
    });

    const response = await get<{
      list: AssetTemplate[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    } | {
      data: {
        list: AssetTemplate[];
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
      };
    }>(`/api/assets/individual?${query.toString()}`);

    return unwrapApiData(response);
  }
);

export const fetchAssetDetail = createAsyncThunk(
  'assets/fetchAssetDetail',
  async (id: string) => {
    const response = await get<AssetTemplate | { data: AssetTemplate }>(`/api/assets/individual/${id}`);
    return unwrapApiData(response);
  }
);

export const createAsset = createAsyncThunk(
  'assets/createAsset',
  async (payload: CreateAssetPayload) => {
    const response = await post<AssetTemplate | { data: AssetTemplate }>('/api/assets/public', payload);
    return unwrapApiData(response);
  }
);

export const createPersonalAsset = createAsyncThunk(
  'assets/createPersonalAsset',
  async (payload: CreateAssetPayload) => {
    const response = await post<AssetTemplate | { data: AssetTemplate }>('/api/assets/individual', {
      ...payload,
      scope: payload.scope ?? 'personal',
      is_detection: payload.is_detection ?? false,
    });

    return unwrapApiData(response);
  }
);

export const updateAsset = createAsyncThunk(
  'assets/updateAsset',
  async ({ id, ...payload }: UpdateAssetPayload) => {
    const response = await post<AssetTemplate | { data: AssetTemplate }>(
      `/api/assets/individual/${id}/update`,
      payload
    );
    return unwrapApiData(response);
  }
);

export const deleteAsset = createAsyncThunk(
  'assets/deleteAsset',
  async (id: string) => {
    await post<{ success: true }>(`/api/assets/individual/${id}/delete`);
    return id;
  }
);

export const syncAssetStatus = createAsyncThunk(
  'assets/syncAssetStatus',
  async (id: string) => {
    const response = await post<AssetTemplate | { data: AssetTemplate }>(`/api/assets/individual/${id}/sync-status`);
    return unwrapApiData(response);
  }
);

export const recordAssetUse = createAsyncThunk(
  'assets/recordAssetUse',
  async (id: string) => {
    await post<{ success: true }>(`/api/assets/public/${id}/use`);
    return id;
  }
);

export const recordAssetExecute = createAsyncThunk(
  'assets/recordAssetExecute',
  async (id: string) => {
    await post<{ success: true }>(`/api/assets/public/${id}/execute`);
    return id;
  }
);

export const searchAssetCategories = createAsyncThunk(
  'assets/searchAssetCategories',
  async (params?: SearchAssetCategoriesParams) => {
    const query = buildSearchParams({
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 20,
      keyword: params?.keyword,
      origin: params?.origin,
      category_type: params?.category_type,
      template_type: params?.template_type,
      user_id: params?.user_id,
      parent_id: params?.parent_id,
    });

    const response = await get<{
      list: AssetCategory[];
      total: number;
      page: number;
      page_size: number;
      total_pages?: number;
    } | {
      data: {
        list: AssetCategory[];
        total: number;
        page: number;
        page_size: number;
        total_pages?: number;
      };
    }>(`/api/assets/categories?${query.toString()}`);

    return unwrapApiData(response);
  }
);

export const fetchAssetCategoryTree = createAsyncThunk(
  'assets/fetchAssetCategoryTree',
  async (params?: AssetCategoryTreeParams) => {
    const query = buildSearchParams({
      origin: params?.origin,
      category_type: params?.category_type,
      template_type: params?.template_type,
      user_id: params?.user_id,
    });
    const response = await get<{ tree: AssetCategory[] } | { data: { tree: AssetCategory[] } }>(
      `/api/assets/categories/tree${query.toString() ? `?${query.toString()}` : ''}`
    );

    return unwrapApiData(response);
  }
);

export const fetchAssetCategoryDetail = createAsyncThunk(
  'assets/fetchAssetCategoryDetail',
  async (id: string) => {
    const response = await get<AssetCategory | { data: AssetCategory }>(`/api/assets/categories/${id}`);
    return unwrapApiData(response);
  }
);

export const createAssetCategory = createAsyncThunk(
  'assets/createAssetCategory',
  async (payload: CreateAssetCategoryPayload) => {
    const response = await post<AssetCategory | { data: AssetCategory }>(
      '/api/assets/categories',
      payload
    );
    return unwrapApiData(response);
  }
);

export const updateAssetCategory = createAsyncThunk(
  'assets/updateAssetCategory',
  async ({ id, ...payload }: UpdateAssetCategoryPayload) => {
    const response = await post<AssetCategory | { data: AssetCategory }>(
      `/api/assets/categories/${id}/update`,
      payload
    );
    return unwrapApiData(response);
  }
);

export const deleteAssetCategory = createAsyncThunk(
  'assets/deleteAssetCategory',
  async (id: string) => {
    await post<{ success: true }>(`/api/assets/categories/${id}/delete`);
    return id;
  }
);

const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    clearAssetsError: (state) => {
      state.error = null;
    },
    setCurrentAsset: (state, action: PayloadAction<AssetTemplate | null>) => {
      state.currentAsset = action.payload;
    },
    setCurrentCategory: (state, action: PayloadAction<AssetCategory | null>) => {
      state.currentCategory = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.list ?? [];
        state.total = action.payload.total ?? 0;
        state.page = action.payload.page ?? 1;
        state.pageSize = action.payload.page_size ?? 20;
        state.totalPages = action.payload.total_pages ?? 0;
      })
      .addCase(searchAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch assets';
      })
      .addCase(searchPublicAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPublicAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.list ?? [];
        state.total = action.payload.total ?? 0;
        state.page = action.payload.page ?? 1;
        state.pageSize = action.payload.page_size ?? 20;
        state.totalPages = action.payload.total_pages ?? 0;
      })
      .addCase(searchPublicAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch public assets';
      })
      .addCase(searchPersonalAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPersonalAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.list ?? [];
        state.total = action.payload.total ?? 0;
        state.page = action.payload.page ?? 1;
        state.pageSize = action.payload.page_size ?? 20;
        state.totalPages = action.payload.total_pages ?? 0;
      })
      .addCase(searchPersonalAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch personal assets';
      })
      .addCase(fetchAssetDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchAssetDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentAsset = action.payload;
      })
      .addCase(fetchAssetDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message || 'Failed to fetch asset detail';
      })
      .addCase(createAsset.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createAsset.fulfilled, (state, action) => {
        state.saving = false;
        state.items.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createAsset.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to create asset';
      })
      .addCase(createPersonalAsset.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createPersonalAsset.fulfilled, (state, action) => {
        state.saving = false;
        state.currentAsset = action.payload;
      })
      .addCase(createPersonalAsset.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to create personal asset';
      })
      .addCase(updateAsset.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateAsset.fulfilled, (state, action) => {
        state.saving = false;
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
        if (state.currentAsset?.id === action.payload.id) {
          state.currentAsset = action.payload;
        }
      })
      .addCase(updateAsset.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to update asset';
      })
      .addCase(deleteAsset.pending, (state, action) => {
        state.deletingId = action.meta.arg;
        state.error = null;
      })
      .addCase(deleteAsset.fulfilled, (state, action) => {
        state.deletingId = null;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        if (state.currentAsset?.id === action.payload) {
          state.currentAsset = null;
        }
      })
      .addCase(deleteAsset.rejected, (state, action) => {
        state.deletingId = null;
        state.error = action.error.message || 'Failed to delete asset';
      })
      .addCase(syncAssetStatus.pending, (state, action) => {
        state.syncingStatusId = action.meta.arg;
        state.error = null;
      })
      .addCase(syncAssetStatus.fulfilled, (state, action) => {
        state.syncingStatusId = null;
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
        if (state.currentAsset?.id === action.payload.id) {
          state.currentAsset = action.payload;
        }
      })
      .addCase(syncAssetStatus.rejected, (state, action) => {
        state.syncingStatusId = null;
        state.error = action.error.message || 'Failed to sync asset status';
      })
      .addCase(recordAssetUse.pending, (state, action) => {
        state.actingId = action.meta.arg;
      })
      .addCase(recordAssetUse.fulfilled, (state) => {
        state.actingId = null;
      })
      .addCase(recordAssetUse.rejected, (state) => {
        state.actingId = null;
      })
      .addCase(recordAssetExecute.pending, (state, action) => {
        state.actingId = action.meta.arg;
      })
      .addCase(recordAssetExecute.fulfilled, (state) => {
        state.actingId = null;
      })
      .addCase(recordAssetExecute.rejected, (state) => {
        state.actingId = null;
      })
      .addCase(searchAssetCategories.pending, (state) => {
        state.loadingCategories = true;
        state.error = null;
      })
      .addCase(searchAssetCategories.fulfilled, (state, action) => {
        state.loadingCategories = false;
        state.categories = action.payload.list ?? [];
      })
      .addCase(searchAssetCategories.rejected, (state, action) => {
        state.loadingCategories = false;
        state.error = action.error.message || 'Failed to fetch asset categories';
      })
      .addCase(fetchAssetCategoryTree.pending, (state) => {
        state.loadingCategoryTree = true;
      })
      .addCase(fetchAssetCategoryTree.fulfilled, (state, action) => {
        state.loadingCategoryTree = false;
        state.categoryTree = action.payload.tree ?? [];
      })
      .addCase(fetchAssetCategoryTree.rejected, (state, action) => {
        state.loadingCategoryTree = false;
        state.error = action.error.message || 'Failed to fetch asset category tree';
      })
      .addCase(fetchAssetCategoryDetail.pending, (state) => {
        state.categoryDetailLoading = true;
      })
      .addCase(fetchAssetCategoryDetail.fulfilled, (state, action) => {
        state.categoryDetailLoading = false;
        state.currentCategory = action.payload;
      })
      .addCase(fetchAssetCategoryDetail.rejected, (state, action) => {
        state.categoryDetailLoading = false;
        state.error = action.error.message || 'Failed to fetch asset category detail';
      })
      .addCase(createAssetCategory.pending, (state) => {
        state.savingCategory = true;
      })
      .addCase(createAssetCategory.fulfilled, (state, action) => {
        state.savingCategory = false;
        state.categories.unshift(action.payload);
      })
      .addCase(createAssetCategory.rejected, (state, action) => {
        state.savingCategory = false;
        state.error = action.error.message || 'Failed to create asset category';
      })
      .addCase(updateAssetCategory.pending, (state) => {
        state.savingCategory = true;
      })
      .addCase(updateAssetCategory.fulfilled, (state, action) => {
        state.savingCategory = false;
        state.categories = state.categories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        );
        if (state.currentCategory?.id === action.payload.id) {
          state.currentCategory = action.payload;
        }
      })
      .addCase(updateAssetCategory.rejected, (state, action) => {
        state.savingCategory = false;
        state.error = action.error.message || 'Failed to update asset category';
      })
      .addCase(deleteAssetCategory.pending, (state, action) => {
        state.deletingCategoryId = action.meta.arg;
      })
      .addCase(deleteAssetCategory.fulfilled, (state, action) => {
        state.deletingCategoryId = null;
        state.categories = state.categories.filter((category) => category.id !== action.payload);
        if (state.currentCategory?.id === action.payload) {
          state.currentCategory = null;
        }
      })
      .addCase(deleteAssetCategory.rejected, (state, action) => {
        state.deletingCategoryId = null;
        state.error = action.error.message || 'Failed to delete asset category';
      });
  },
});

export const { clearAssetsError, setCurrentAsset, setCurrentCategory } = assetsSlice.actions;

export default assetsSlice.reducer;
